// Photo trail + mosaic tool for georeferenced camera-still sequences (built for the Voyis
// underwater camera, but not Voyis-specific in principle -- anything indexed into the
// server's camera_frames table via /api/photos/voyis/index works here). Phase 3 (SfM) is
// separate -- see /home/ubuntu/.claude/plans/frolicking-pondering-lovelace.md.

import { reportTaskStatus } from "../../core/task-status.js";

const TOOL_ID = "photo-mosaic";
const VOYIS_PREFIX = "source-data/Voyis/Processed/";

// Same "module-level listener + a var mount() reads at open time" pattern as
// opus-gnss/slip-delivery -- avoids missing the event if this tool wasn't
// mounted yet when the user last switched projects.
let _lastBroadcastProjectId = "";
document.addEventListener("hydrotools:project-changed", (e) => {
  _lastBroadcastProjectId = String(e.detail?.projectId || "");
});

function esc(v) {
  return String(v ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function fmtWhen(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
}

// Low-res viridis approximation, same 5-stop anchor set used elsewhere in this
// app (see surfaceStyleParams in slip-delivery) -- kept consistent rather than
// inventing a new palette for this one tool.
const VIRIDIS_STOPS = [
  [0.00, 68, 1, 84], [0.25, 59, 82, 139], [0.50, 33, 144, 140],
  [0.75, 93, 200, 99], [1.00, 253, 231, 37],
];
function viridisColor(t) {
  const c = Math.max(0, Math.min(1, t));
  for (let i = 0; i < VIRIDIS_STOPS.length - 1; i++) {
    const [t0, r0, g0, b0] = VIRIDIS_STOPS[i];
    const [t1, r1, g1, b1] = VIRIDIS_STOPS[i + 1];
    if (c >= t0 && c <= t1) {
      const f = (c - t0) / (t1 - t0 || 1);
      return `rgb(${Math.round(r0 + (r1 - r0) * f)},${Math.round(g0 + (g1 - g0) * f)},${Math.round(b0 + (b1 - b0) * f)})`;
    }
  }
  const [, r, g, b] = VIRIDIS_STOPS[VIRIDIS_STOPS.length - 1];
  return `rgb(${r},${g},${b})`;
}

function createRuntime(app) {
  let containerEl = null;
  let currentCode = "";
  let frames = []; // GeoJSON features from /api/photos/voyis/frames
  let framesLoading = false;
  let layerId = null;
  let indexJobId = null;
  let indexPollTimer = null;
  let indexStatus = null; // {status, progress:{done,total}, error, result}
  let viewerEl = null;
  let viewerFrame = null;
  let viewerUrl = "";
  let viewerLoading = false;
  let viewerAdjust = { brightness: 1, contrast: 1, gamma: 1 };
  let viewerApplying = false;

  const MOSAIC_PANE_NAME = "photo-mosaic-raster-pane";
  let mosaicLayerId = null;
  let mosaicSettings = { brightness: 1, contrast: 1, gamma: 1 };
  let mosaicCacheBust = 0;
  let mosaicBuildJobId = null;
  let mosaicBuildStatus = null;
  let mosaicBuildPollTimer = null;
  let mosaicRangeFrom = null;
  let mosaicRangeTo = null;
  let mosaicStride = 1;

  // Mirrors src/tools/slip-delivery/index.js's taskStatus/taskIdle/taskDone -- reports into
  // the system status bar (src/core/ui/shell.js) so a multi-minute build is visible without
  // keeping this tool's own panel open, and survives switching tools/tabs. "running" pins the
  // entry until idle/done/error; taskDone leaves a visible summary for a few seconds instead
  // of silently vanishing (see task-status.js/shell.js for the shared contract).
  function taskStatus(message, progress = null, running = true) {
    reportTaskStatus(app, TOOL_ID, { label: "Photo Mosaic", message, progress, status: running ? "running" : "idle", projectCode: currentCode || null });
  }
  function taskIdle() { taskStatus("", null, false); }
  function taskDone(message, ok = true) {
    reportTaskStatus(app, TOOL_ID, { label: "Photo Mosaic", message, progress: 100, status: ok ? "done" : "error", projectCode: currentCode || null });
  }

  // Same brightness()/contrast() CSS-filter mechanism as the Surfaces tool's hillshade
  // sliders (buildPaneFilter) -- applied live on every slider tick, no server round-trip.
  // "gamma" here maps to CSS brightness() same as Surfaces does (there's no native CSS gamma
  // filter); the server-side bake (frame-adjust) applies a real gamma curve, so live preview
  // and baked result are close but not pixel-identical.
  function buildFilterCss(s) {
    const parts = [];
    if (s.gamma !== 1) parts.push(`brightness(${s.gamma.toFixed(2)})`);
    if (s.brightness !== 1) parts.push(`brightness(${s.brightness.toFixed(2)})`);
    if (s.contrast !== 1) parts.push(`contrast(${s.contrast.toFixed(2)})`);
    return parts.join(" ");
  }

  function mosaicTileUrl(code) {
    return `/api/point-cloud/mosaic2d/${encodeURIComponent(code)}/tiles/{z}/{x}/{y}.png?attr=photomosaic&_v=${mosaicCacheBust}`;
  }

  function updateMosaicLayer() {
    // EMERGENCY DISABLE (2026-09-22), same incident as updateMapLayer above -- this is an
    // XYZ raster layer in its own pane, not the shared canvas, so it's a less likely
    // suspect than the circleMarker trail, but disabling it too until the actual mechanism
    // is confirmed rather than assumed safe. See updateMapLayer's comment for the full story.
    const layers = app?.services?.layers;
    const map = app?.map?.instance;
    if (!layers || !map) return;
    if (mosaicLayerId) { layers.removeLayer(mosaicLayerId); mosaicLayerId = null; }
    return;
    // eslint-disable-next-line no-unreachable
    if (!map.getPane(MOSAIC_PANE_NAME)) {
      const pane = map.createPane(MOSAIC_PANE_NAME);
      pane.style.zIndex = 250; // above the base tile layer (200), below markers/popups (600+)
    }
    mosaicLayerId = layers.addLayer(TOOL_ID, {
      type: "xyz",
      name: "Camera Mosaic (quick-look)",
      group: "Survey Planning",
      opacity: 1,
      visible: true,
      url: mosaicTileUrl(currentCode),
      options: { pane: MOSAIC_PANE_NAME, maxNativeZoom: 22, minZoom: 12, maxZoom: 26 },
    });
    applyMosaicPaneFilter();
  }

  function applyMosaicPaneFilter() {
    const pane = app?.map?.instance?.getPane?.(MOSAIC_PANE_NAME);
    if (pane) pane.style.filter = buildFilterCss(mosaicSettings);
  }

  function frameNumRange() {
    const nums = frames.map((f) => f.properties.frameNum).filter(Number.isFinite);
    if (!nums.length) return [1, 1];
    return [Math.min(...nums), Math.max(...nums)];
  }

  function selectedFrameKeys() {
    const [lo, hi] = frameNumRange();
    const from = Number.isFinite(mosaicRangeFrom) ? mosaicRangeFrom : lo;
    const to = Number.isFinite(mosaicRangeTo) ? mosaicRangeTo : hi;
    const stride = Math.max(1, Math.round(mosaicStride) || 1);
    return frames
      .filter((f) => {
        const n = f.properties.frameNum;
        return Number.isFinite(n) && n >= from && n <= to;
      })
      .filter((_, i) => i % stride === 0)
      .map((f) => f.properties.sourceKey);
  }

  async function startMosaicBuild() {
    if (!currentCode || mosaicBuildJobId || !frames.length) return;
    const frameKeys = selectedFrameKeys();
    if (!frameKeys.length) { app.ui.toast("No frames match the selected range."); return; }
    if (!confirm(`Build a quick-look mosaic from ${frameKeys.length} frame(s)? This warps each photo individually and can take a while for large selections.`)) return;
    try {
      const resp = await fetch("/api/photos/voyis/mosaic/build", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: currentCode, frameKeys }),
      });
      const data = await resp.json();
      // A 409 here means another tab/session already has a build running for this project --
      // resume tracking that one instead of erroring, same as the active-job recovery below.
      if (!data.ok && resp.status === 409 && data.jobId) {
        mosaicBuildJobId = data.jobId;
        mosaicBuildStatus = { status: "running", progress: { done: 0, total: 0 } };
        app.ui.toast("A mosaic build was already running for this project — tracking it.");
        render();
        pollMosaicBuild();
        return;
      }
      if (!data.ok) throw new Error(data.error || "Failed to start mosaic build.");
      mosaicBuildJobId = data.jobId;
      mosaicBuildStatus = { status: "running", progress: { done: 0, total: frameKeys.length } };
      taskStatus(`Building mosaic: 0 / ${frameKeys.length} frame(s)…`, 0);
      render();
      pollMosaicBuild();
    } catch (err) {
      app.ui.toast(`Mosaic build failed to start: ${err.message}`);
    }
  }

  // On mount/project-switch, ask the server whether a build is already running for this
  // project (see /api/photos/voyis/mosaic/build/active) instead of only ever learning about
  // one from the jobId the POST above returned -- that jobId lived only in this closure, so a
  // page refresh or reopening the tool mid-build had no way to reconnect to it: the button
  // looked stuck on "Building..." (because the client had already moved on / lost track)
  // while the server kept working, with no way to tell the two states apart from the UI alone.
  async function recoverActiveMosaicBuild(code) {
    if (!code) return;
    try {
      const resp = await fetch(`/api/photos/voyis/mosaic/build/active?code=${encodeURIComponent(code)}`);
      const data = await resp.json();
      if (data.ok && data.jobId && code === currentCode) {
        mosaicBuildJobId = data.jobId;
        mosaicBuildStatus = { status: "running", progress: { done: 0, total: 0 } };
        render();
        pollMosaicBuild();
      }
    } catch {}
  }

  function pollMosaicBuild() {
    if (mosaicBuildPollTimer) clearTimeout(mosaicBuildPollTimer);
    mosaicBuildPollTimer = setTimeout(async () => {
      if (!mosaicBuildJobId) return;
      try {
        const resp = await fetch(`/api/photos/voyis/mosaic/build/${encodeURIComponent(mosaicBuildJobId)}`);
        const data = await resp.json();
        if (!data.ok) throw new Error(data.error || "Job not found.");
        mosaicBuildStatus = data;
        const p = data.progress;
        if (data.status === "running") {
          taskStatus(`Building mosaic: ${p?.done ?? 0} / ${p?.total ?? "?"} frame(s)…`, p?.total ? Math.round((p.done / p.total) * 100) : null);
          render();
          pollMosaicBuild();
        } else {
          mosaicBuildJobId = null;
          if (data.status === "done") {
            const msg = `Mosaic built: ${data.result?.built ?? 0} of ${data.result?.total ?? 0} frame(s).`;
            app.ui.toast(msg);
            taskDone(msg, true);
            mosaicCacheBust += 1;
            updateMosaicLayer();
          } else {
            const msg = `Mosaic build failed: ${data.error || "unknown error"}`;
            app.ui.toast(msg);
            taskDone(msg, false);
          }
          render();
        }
      } catch (err) {
        // The job genuinely vanished (server restart wipes the in-memory job map) rather than
        // this being a transient network blip -- a 404 here is a real, if rare, "we lost
        // track" case, distinct from every other fetch failure, so say so plainly instead of
        // leaving the button stuck or implying the build failed outright.
        const lost = /job not found/i.test(String(err?.message || ""));
        mosaicBuildJobId = null;
        const msg = lost
          ? "Lost track of the mosaic build (server may have restarted) — check the map, it may already be complete."
          : `Mosaic build poll failed: ${err.message}`;
        app.ui.toast(msg);
        taskDone(msg, false);
        render();
      }
    }, 2000);
  }

  async function applyFrameAdjust() {
    if (!viewerFrame || viewerApplying) return;
    viewerApplying = true;
    renderViewer();
    try {
      const resp = await fetch("/api/photos/voyis/mosaic/frame-adjust", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code: currentCode,
          sourceKey: viewerFrame.properties.sourceKey,
          brightness: viewerAdjust.brightness, contrast: viewerAdjust.contrast, gamma: viewerAdjust.gamma,
        }),
      });
      const data = await resp.json();
      if (!data.ok) throw new Error(data.error || "Failed to apply.");
      app.ui.toast(`Frame ${viewerFrame.properties.frameNum ?? ""} updated in the mosaic.`);
      mosaicCacheBust += 1;
      updateMosaicLayer();
    } catch (err) {
      app.ui.toast(`Apply failed: ${err.message}`);
    }
    viewerApplying = false;
    renderViewer();
  }

  function altRange() {
    const alts = frames.map((f) => f.properties.altM).filter((v) => Number.isFinite(v));
    if (!alts.length) return [0, 1];
    return [Math.min(...alts), Math.max(...alts)];
  }

  function styleBuilder(feature) {
    const [lo, hi] = altRange();
    const alt = feature.properties.altM;
    const t = Number.isFinite(alt) && hi > lo ? (alt - lo) / (hi - lo) : 0.5;
    const isSystemClock = feature.properties.timestampSource === "system";
    return {
      radius: 4,
      color: isSystemClock ? "#f87171" : "#0f172a",
      weight: isSystemClock ? 2 : 1,
      fillColor: viridisColor(t),
      fillOpacity: 0.85,
    };
  }

  function popupBuilder(feature) {
    const p = feature.properties;
    return `
      <div style="font:12px/1.4 -apple-system,BlinkMacSystemFont,sans-serif;min-width:160px;">
        <div style="font-weight:600;">Frame ${esc(p.frameNum ?? "?")}</div>
        <div style="color:#64748b;">${esc(fmtWhen(p.capturedAt))}</div>
        ${Number.isFinite(p.altM) ? `<div>Altitude: ${p.altM.toFixed(2)} m</div>` : ""}
        ${p.timestampSource === "system" ? `<div style="color:#f87171;">System clock (PPS sync lost)</div>` : ""}
        <button type="button" data-photo-view="${esc(p.sourceKey)}" class="btn" style="margin-top:6px;font-size:11px;padding:3px 8px;">View photo</button>
      </div>
    `;
  }

  function updateMapLayer() {
    // EMERGENCY DISABLE (2026-09-22): this added ~1,541 circleMarkers to the map's single
    // shared canvas renderer (preferCanvas: true, every vector layer app-wide shares it --
    // see the matching comment in core/services/layers.js and slip-delivery/index.js's own
    // canvasRenderer). Right after this shipped, Safe Harbor slip polygons (drawn on that
    // same shared canvas) stopped reprojecting on zoom app-wide -- stuck at their pre-zoom
    // screen size while tiles zoomed normally underneath. Disabling this render path
    // entirely until the shared-canvas interaction is understood and fixed properly; the
    // indexing/data side (camera_frames, the photo viewer) is untouched. Do not re-enable
    // by just flipping this back on without addressing the shape-count issue -- a smaller
    // marker count might be safe, but that needs verifying, not assuming.
    const layers = app?.services?.layers;
    if (!layers) return;
    if (layerId) { layers.removeLayer(layerId); layerId = null; }
    return;
    // eslint-disable-next-line no-unreachable
    const featureCollection = { type: "FeatureCollection", features: frames };
    layerId = layers.addLayer(TOOL_ID, {
      type: "geojson-vector",
      name: "Camera Trail",
      group: "Survey Planning",
      opacity: 1,
      visible: true,
      data: featureCollection,
      options: { styleBuilder, popupBuilder, pointsAsCircles: true },
    });
  }

  async function loadFrames(code) {
    if (!code) { frames = []; updateMapLayer(); render(); return; }
    framesLoading = true;
    render();
    try {
      const resp = await fetch(`/api/photos/voyis/frames?code=${encodeURIComponent(code)}`);
      const data = await resp.json();
      frames = data.ok && Array.isArray(data.features) ? data.features : [];
    } catch {
      frames = [];
    }
    framesLoading = false;
    updateMapLayer();
    render();
  }

  async function startIndexing() {
    if (!currentCode || indexJobId) return;
    try {
      const resp = await fetch("/api/photos/voyis/index", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: currentCode, prefix: VOYIS_PREFIX }),
      });
      const data = await resp.json();
      if (!data.ok && resp.status === 409 && data.jobId) {
        indexJobId = data.jobId;
        indexStatus = { status: "running", progress: { done: 0, total: 0 } };
        app.ui.toast("An index job was already running for this project — tracking it.");
        render();
        pollIndexJob();
        return;
      }
      if (!data.ok) throw new Error(data.error || "Failed to start indexing job.");
      indexJobId = data.jobId;
      indexStatus = { status: "running", progress: { done: 0, total: 0 } };
      taskStatus("Indexing frames…", 0);
      render();
      pollIndexJob();
    } catch (err) {
      app.ui.toast(`Indexing failed to start: ${err.message}`);
    }
  }

  // Same recovery rationale as recoverActiveMosaicBuild above.
  async function recoverActiveIndexJob(code) {
    if (!code) return;
    try {
      const resp = await fetch(`/api/photos/voyis/index/active?code=${encodeURIComponent(code)}`);
      const data = await resp.json();
      if (data.ok && data.jobId && code === currentCode) {
        indexJobId = data.jobId;
        indexStatus = { status: "running", progress: { done: 0, total: 0 } };
        render();
        pollIndexJob();
      }
    } catch {}
  }

  function pollIndexJob() {
    if (indexPollTimer) clearTimeout(indexPollTimer);
    indexPollTimer = setTimeout(async () => {
      if (!indexJobId) return;
      try {
        const resp = await fetch(`/api/photos/voyis/index/${encodeURIComponent(indexJobId)}`);
        const data = await resp.json();
        if (!data.ok) throw new Error(data.error || "Job not found.");
        indexStatus = data;
        const p = data.progress;
        if (data.status === "running") {
          taskStatus(`Indexing frames: ${p?.done ?? 0} / ${p?.total ?? "?"}…`, p?.total ? Math.round((p.done / p.total) * 100) : null);
          render();
          pollIndexJob();
        } else {
          const finishedCode = currentCode;
          indexJobId = null;
          if (data.status === "done") {
            const msg = `Indexed ${data.result?.indexed ?? 0} of ${data.result?.total ?? 0} frame(s).`;
            app.ui.toast(msg);
            taskDone(msg, true);
            loadFrames(finishedCode);
          } else {
            const msg = `Indexing failed: ${data.error || "unknown error"}`;
            app.ui.toast(msg);
            taskDone(msg, false);
          }
          render();
        }
      } catch (err) {
        const lost = /job not found/i.test(String(err?.message || ""));
        indexJobId = null;
        const msg = lost
          ? "Lost track of the index job (server may have restarted) — click Index Frames again if the count looks stale."
          : `Indexing job poll failed: ${err.message}`;
        app.ui.toast(msg);
        taskDone(msg, false);
        render();
      }
    }, 1500);
  }

  // ── Photo viewer (simple <img>, not the streetview-360 sphere viewer --
  // that's 360-panorama specific and wrong for a flat still) ─────────────────
  function closeViewer() {
    if (viewerEl) { viewerEl.remove(); viewerEl = null; }
    viewerFrame = null;
    viewerUrl = "";
  }

  function frameIndexInSequence(sourceKey) {
    return frames.findIndex((f) => f.properties.sourceKey === sourceKey);
  }

  async function openViewer(sourceKey) {
    const idx = frameIndexInSequence(sourceKey);
    if (idx === -1) return;
    viewerFrame = frames[idx];
    viewerUrl = "";
    viewerLoading = true;
    viewerAdjust = { brightness: 1, contrast: 1, gamma: 1 };
    renderViewer();
    try {
      const resp = await fetch("/api/s3/presign/batch-download", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ keys: [sourceKey] }),
      });
      const data = await resp.json();
      viewerUrl = Array.isArray(data.urls) ? (data.urls[0] || "") : "";
    } catch {
      viewerUrl = "";
    }
    viewerLoading = false;
    renderViewer();
  }

  function stepViewer(delta) {
    if (!viewerFrame) return;
    const idx = frameIndexInSequence(viewerFrame.properties.sourceKey);
    const next = frames[idx + delta];
    if (next) openViewer(next.properties.sourceKey);
  }

  function renderViewer() {
    if (!viewerFrame) { closeViewer(); return; }
    if (!viewerEl) {
      viewerEl = document.createElement("div");
      viewerEl.style.cssText = "position:fixed;inset:0;z-index:100000;background:rgba(2,6,23,0.85);display:flex;align-items:center;justify-content:center;font:12px/1.5 -apple-system,BlinkMacSystemFont,sans-serif;";
      viewerEl.addEventListener("click", (e) => { if (e.target === viewerEl) closeViewer(); });
      document.body.appendChild(viewerEl);
      document.addEventListener("keydown", viewerKeydown);
    }
    const p = viewerFrame.properties;
    viewerEl.innerHTML = `
      <div style="max-width:min(90vw,1100px);max-height:90vh;display:flex;flex-direction:column;gap:8px;align-items:center;">
        <div style="color:#e2e8f0;display:flex;gap:12px;align-items:center;">
          <span style="font-weight:600;">Frame ${esc(p.frameNum ?? "?")}</span>
          <span style="color:#94a3b8;">${esc(fmtWhen(p.capturedAt))}</span>
          ${Number.isFinite(p.altM) ? `<span style="color:#94a3b8;">${p.altM.toFixed(2)} m</span>` : ""}
          <button type="button" data-viewer-close class="btn" style="margin-left:12px;">✕ Close</button>
        </div>
        <div style="position:relative;display:flex;align-items:center;justify-content:center;background:#000;border-radius:6px;overflow:hidden;">
          ${viewerLoading
            ? `<div style="color:#94a3b8;padding:80px;">Loading…</div>`
            : viewerUrl
              ? `<img data-viewer-img src="${esc(viewerUrl)}" style="max-width:min(90vw,1100px);max-height:76vh;display:block;filter:${esc(buildFilterCss(viewerAdjust))};" />`
              : `<div style="color:#f87171;padding:80px;">Failed to load photo.</div>`}
        </div>
        ${viewerUrl ? `
          <div style="display:flex;gap:14px;align-items:center;color:#e2e8f0;font-size:11px;flex-wrap:wrap;justify-content:center;background:rgba(15,23,42,0.7);padding:6px 12px;border-radius:6px;">
            <label style="display:flex;gap:5px;align-items:center;">Brightness
              <input type="range" data-viewer-adjust="brightness" min="0.2" max="3" step="0.05" value="${viewerAdjust.brightness}" style="width:90px;" />
            </label>
            <label style="display:flex;gap:5px;align-items:center;">Contrast
              <input type="range" data-viewer-adjust="contrast" min="0.2" max="3" step="0.05" value="${viewerAdjust.contrast}" style="width:90px;" />
            </label>
            <label style="display:flex;gap:5px;align-items:center;">Gamma
              <input type="range" data-viewer-adjust="gamma" min="0.2" max="3" step="0.05" value="${viewerAdjust.gamma}" style="width:90px;" />
            </label>
            <button type="button" data-viewer-reset-adjust class="btn" style="font-size:10px;padding:2px 8px;">Reset</button>
            <button type="button" data-viewer-apply class="btn primary" style="font-size:10px;padding:2px 8px;" ${viewerApplying ? "disabled" : ""}>
              ${viewerApplying ? "Applying…" : "Apply to Mosaic"}
            </button>
          </div>
        ` : ""}
        <div style="display:flex;gap:8px;">
          <button type="button" data-viewer-prev class="btn">&larr; Prev</button>
          <button type="button" data-viewer-next class="btn">Next &rarr;</button>
        </div>
      </div>
    `;
    viewerEl.querySelector("[data-viewer-close]")?.addEventListener("click", closeViewer);
    viewerEl.querySelector("[data-viewer-prev]")?.addEventListener("click", () => stepViewer(-1));
    viewerEl.querySelector("[data-viewer-next]")?.addEventListener("click", () => stepViewer(1));
    viewerEl.querySelectorAll("[data-viewer-adjust]").forEach((input) => {
      input.addEventListener("input", () => {
        const key = input.getAttribute("data-viewer-adjust");
        viewerAdjust[key] = Number(input.value) || 1;
        const img = viewerEl.querySelector("[data-viewer-img]");
        if (img) img.style.filter = buildFilterCss(viewerAdjust);
      });
    });
    viewerEl.querySelector("[data-viewer-reset-adjust]")?.addEventListener("click", () => {
      viewerAdjust = { brightness: 1, contrast: 1, gamma: 1 };
      renderViewer();
    });
    viewerEl.querySelector("[data-viewer-apply]")?.addEventListener("click", applyFrameAdjust);
  }

  function viewerKeydown(e) {
    if (e.key === "Escape") closeViewer();
    else if (e.key === "ArrowLeft") stepViewer(-1);
    else if (e.key === "ArrowRight") stepViewer(1);
  }

  // Delegated click handler for the map popup's "View photo" button -- popups
  // are re-created by Leaflet on open, so binding once at the document level
  // (rather than per-popup) is the reliable way to catch this.
  function onDocumentClick(e) {
    const btn = e.target?.closest?.("[data-photo-view]");
    if (btn) openViewer(btn.getAttribute("data-photo-view"));
  }

  function summaryLine() {
    if (framesLoading) return "Loading indexed frames…";
    if (!frames.length) return "No frames indexed yet for this project.";
    const withGps = frames.length;
    const systemCount = frames.filter((f) => f.properties.timestampSource === "system").length;
    const times = frames.map((f) => f.properties.capturedAt).filter(Boolean).sort();
    const span = times.length >= 2
      ? `${fmtWhen(times[0])} → ${fmtWhen(times[times.length - 1])}`
      : "";
    return `${withGps.toLocaleString()} frame(s) indexed${systemCount ? `, ${systemCount} on system clock (red ring)` : ""}.${span ? ` ${span}.` : ""}`;
  }

  function render() {
    if (!containerEl) return;
    const indexing = !!indexJobId;
    const progress = indexStatus?.progress;
    const pct = progress?.total ? Math.round((progress.done / progress.total) * 100) : 0;

    containerEl.innerHTML = `
      <div class="small" style="color:var(--muted);padding-bottom:8px;">
        Plots a georeferenced camera-still sequence (e.g. Voyis underwater camera) as a
        trail on the map. Build the index once per project; re-run it any time new frames
        land in <code>${esc(VOYIS_PREFIX)}</code>.
      </div>

      ${!currentCode ? `
        <div class="small" style="color:#f59e0b;padding:8px 0;">Select a project first.</div>
      ` : `
        <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;padding-bottom:8px;">
          <button type="button" data-index-btn class="btn" ${indexing ? "disabled" : ""}>
            ${indexing ? "Indexing…" : "Index Frames"}
          </button>
          ${indexing ? `<span class="small" style="color:var(--muted);">${progress?.done ?? 0} / ${progress?.total ?? "?"} (${pct}%)</span>` : ""}
        </div>
        <div class="small" style="color:var(--muted);padding-bottom:4px;">${esc(summaryLine())}</div>
        ${indexStatus?.result?.errors?.length ? `
          <details class="small" style="color:#f59e0b;">
            <summary style="cursor:pointer;">${indexStatus.result.errors.length} frame(s) failed to index</summary>
            <pre style="white-space:pre-wrap;max-height:160px;overflow:auto;background:rgba(2,6,23,0.35);padding:6px;border-radius:4px;">${esc(JSON.stringify(indexStatus.result.errors, null, 2))}</pre>
          </details>
        ` : ""}
        <div class="small" style="color:var(--muted);padding-top:4px;">Click a pin on the map to view that photo.</div>

        <div style="border-top:1px solid rgba(148,163,184,0.15);margin-top:10px;padding-top:8px;">
          <div style="font-weight:600;font-size:11px;margin-bottom:4px;">Quick-look mosaic</div>
          <div class="small" style="color:var(--muted);padding-bottom:6px;">
            Nadir-projected warp per frame — footprint size uses an estimated sensor size
            (not confirmed by Voyis), and there's no orientation data, so this is approximate,
            not survey-grade.
          </div>
          ${(() => {
            const [lo, hi] = frameNumRange();
            const from = Number.isFinite(mosaicRangeFrom) ? mosaicRangeFrom : lo;
            const to = Number.isFinite(mosaicRangeTo) ? mosaicRangeTo : hi;
            const selectedCount = selectedFrameKeys().length;
            return `
              <div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap;font-size:11px;padding-bottom:6px;">
                <label style="display:flex;gap:5px;align-items:center;">From #
                  <input type="number" data-mosaic-range="from" min="${lo}" max="${hi}" value="${from}" style="width:64px;" />
                </label>
                <label style="display:flex;gap:5px;align-items:center;">To #
                  <input type="number" data-mosaic-range="to" min="${lo}" max="${hi}" value="${to}" style="width:64px;" />
                </label>
                <label style="display:flex;gap:5px;align-items:center;">Every Nth
                  <input type="number" data-mosaic-range="stride" min="1" max="50" value="${mosaicStride}" style="width:52px;" />
                </label>
                <span style="color:var(--muted);">${selectedCount.toLocaleString()} frame(s) selected (of ${frames.length.toLocaleString()} indexed)</span>
              </div>
            `;
          })()}
          <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;padding-bottom:8px;">
            <button type="button" data-mosaic-build-btn class="btn" ${mosaicBuildJobId ? "disabled" : ""}>
              ${mosaicBuildJobId ? "Building…" : "Build Mosaic"}
            </button>
            ${mosaicBuildJobId ? `<span class="small" style="color:var(--muted);">${mosaicBuildStatus?.progress?.done ?? 0} / ${mosaicBuildStatus?.progress?.total ?? "?"}</span>` : ""}
          </div>
          ${mosaicBuildStatus?.result?.errors?.length ? `
            <details class="small" style="color:#f59e0b;">
              <summary style="cursor:pointer;">${mosaicBuildStatus.result.errors.length} frame(s) failed to build</summary>
              <pre style="white-space:pre-wrap;max-height:160px;overflow:auto;background:rgba(2,6,23,0.35);padding:6px;border-radius:4px;">${esc(JSON.stringify(mosaicBuildStatus.result.errors, null, 2))}</pre>
            </details>
          ` : ""}
          <div style="display:flex;gap:14px;align-items:center;flex-wrap:wrap;font-size:11px;padding-top:4px;">
            <label style="display:flex;gap:5px;align-items:center;">Brightness
              <input type="range" data-mosaic-adjust="brightness" min="0.2" max="3" step="0.05" value="${mosaicSettings.brightness}" style="width:80px;" />
            </label>
            <label style="display:flex;gap:5px;align-items:center;">Contrast
              <input type="range" data-mosaic-adjust="contrast" min="0.2" max="3" step="0.05" value="${mosaicSettings.contrast}" style="width:80px;" />
            </label>
            <label style="display:flex;gap:5px;align-items:center;">Gamma
              <input type="range" data-mosaic-adjust="gamma" min="0.2" max="3" step="0.05" value="${mosaicSettings.gamma}" style="width:80px;" />
            </label>
          </div>
          <div class="small" style="color:var(--muted);padding-top:2px;">Sliders here preview live for the whole mosaic (not saved). Per-image edits with "Apply to Mosaic" are in the photo viewer.</div>
        </div>
      `}
    `;

    containerEl.querySelector("[data-index-btn]")?.addEventListener("click", startIndexing);
    containerEl.querySelector("[data-mosaic-build-btn]")?.addEventListener("click", startMosaicBuild);
    containerEl.querySelector('[data-mosaic-range="from"]')?.addEventListener("change", (e) => { mosaicRangeFrom = Number(e.target.value) || null; render(); });
    containerEl.querySelector('[data-mosaic-range="to"]')?.addEventListener("change", (e) => { mosaicRangeTo = Number(e.target.value) || null; render(); });
    containerEl.querySelector('[data-mosaic-range="stride"]')?.addEventListener("change", (e) => { mosaicStride = Math.max(1, Number(e.target.value) || 1); render(); });
    containerEl.querySelectorAll("[data-mosaic-adjust]").forEach((input) => {
      input.addEventListener("input", () => {
        const key = input.getAttribute("data-mosaic-adjust");
        mosaicSettings[key] = Number(input.value) || 1;
        applyMosaicPaneFilter();
      });
    });
  }

  function onProjectChanged(e) {
    const code = String(e.detail?.projectId || "");
    currentCode = code;
    indexJobId = null;
    indexStatus = null;
    mosaicBuildJobId = null;
    mosaicBuildStatus = null;
    mosaicRangeFrom = null;
    mosaicRangeTo = null;
    closeViewer();
    loadFrames(code);
    updateMosaicLayer();
    recoverActiveIndexJob(code);
    recoverActiveMosaicBuild(code);
  }

  function mount(nextContainer, { initialProjectId = "" } = {}) {
    containerEl = nextContainer;
    if (initialProjectId) currentCode = initialProjectId;
    else if (!currentCode && _lastBroadcastProjectId) currentCode = _lastBroadcastProjectId;
    document.addEventListener("hydrotools:project-changed", onProjectChanged);
    document.addEventListener("click", onDocumentClick);
    if (currentCode) {
      loadFrames(currentCode);
      updateMosaicLayer();
      recoverActiveIndexJob(currentCode);
      recoverActiveMosaicBuild(currentCode);
    }
    render();
  }

  function destroy() {
    document.removeEventListener("hydrotools:project-changed", onProjectChanged);
    document.removeEventListener("click", onDocumentClick);
    document.removeEventListener("keydown", viewerKeydown);
    if (indexPollTimer) { clearTimeout(indexPollTimer); indexPollTimer = null; }
    if (mosaicBuildPollTimer) { clearTimeout(mosaicBuildPollTimer); mosaicBuildPollTimer = null; }
    closeViewer();
    app?.services?.layers?.removeLayersByTool?.(TOOL_ID);
    layerId = null;
    mosaicLayerId = null;
  }

  return { mount, destroy };
}

export const photoMosaicTool = {
  id: TOOL_ID,
  name: "Photo Mosaic",
  icon: "&#128247;",
  init(app) {
    this._rt = this._rt ?? createRuntime(app);
  },
  destroy() {
    this._rt?.destroy?.();
    this._rt = null;
  },
  renderPanel(app, containerEl) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.mount(containerEl);
  },
};
